import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminGetCorporateTeacherByEmail,
  adminListCorporateSchools,
  adminListCorporateTeachersBySchool,
  adminSetCorporateTeacherStatus,
  adminUpsertCorporateTeacher,
} from "./adminApi";
import "./AdminDashboard.css";

const ROLE_OPTIONS = ["teacher", "admin"];

function displayValue(value) {
  return value === 0 || value === false || value ? String(value) : "-";
}

function getTeacherPayload(response) {
  return response?.data?.teacher || response?.teacher || response?.data || response || null;
}

function getTeachersPayload(response) {
  return response?.data?.teachers || response?.teachers || response?.data || [];
}

function getSchoolsPayload(response) {
  return response?.data?.schools || response?.schools || [];
}

function getSchoolId(school) {
  return school?.school_id || school?.RowKey || "";
}

function getSchoolLabel(school) {
  const id = getSchoolId(school);
  const name = school?.school_name || "";
  return name && id ? `${name} (${id})` : id;
}

function normalizeTeacher(row) {
  return row || {};
}

function TeacherSummary({ teacher }) {
  if (!teacher) {
    return <div className="admin-meta">No teacher loaded yet.</div>;
  }

  return (
    <div className="admin-summary-grid">
      <div className="admin-summary-item"><b>school_id:</b> {displayValue(teacher.school_id)}</div>
      <div className="admin-summary-item"><b>teacher_email:</b> {displayValue(teacher.teacher_email)}</div>
      <div className="admin-summary-item"><b>teacher_id:</b> {displayValue(teacher.teacher_id)}</div>
      <div className="admin-summary-item"><b>display_name:</b> {displayValue(teacher.display_name)}</div>
      <div className="admin-summary-item"><b>role:</b> {displayValue(teacher.role)}</div>
      <div className="admin-summary-item"><b>is_active:</b> {displayValue(teacher.is_active)}</div>
      <div className="admin-summary-item"><b>created_at:</b> {displayValue(teacher.created_at)}</div>
      <div className="admin-summary-item"><b>updated_at:</b> {displayValue(teacher.updated_at)}</div>
      <div className="admin-summary-item"><b>updated_by:</b> {displayValue(teacher.updated_by)}</div>
      <div className="admin-summary-item"><b>last_login_at:</b> {displayValue(teacher.last_login_at)}</div>
    </div>
  );
}

export default function AdminCorporateTeachersPage() {
  const navigate = useNavigate();
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupTeacher, setLookupTeacher] = useState(null);
  const [schoolSearchId, setSchoolSearchId] = useState("");
  const [currentSchoolId, setCurrentSchoolId] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [formSchoolId, setFormSchoolId] = useState("");
  const [formTeacherEmail, setFormTeacherEmail] = useState("");
  const [formTeacherId, setFormTeacherId] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formRole, setFormRole] = useState("teacher");
  const [formIsActive, setFormIsActive] = useState("true");
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadSchoolOptions() {
    setLoadingSchools(true);
    try {
      const response = await adminListCorporateSchools();
      const rows = getSchoolsPayload(response);
      setSchoolOptions(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setSchoolOptions([]);
      setError(err.message || "Failed to load school list.");
    } finally {
      setLoadingSchools(false);
    }
  }

  useEffect(() => {
    loadSchoolOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/admin/login", { replace: true });
      return true;
    }
    return false;
  }

  function fillTeacherForm(teacher) {
    const row = normalizeTeacher(teacher);
    setSelectedTeacher(row);
    setFormSchoolId(row.school_id || currentSchoolId || schoolSearchId || "");
    setFormTeacherEmail(row.teacher_email || "");
    setFormTeacherId(row.teacher_id || "");
    setFormDisplayName(row.display_name || "");
    setFormRole(ROLE_OPTIONS.includes(row.role) ? row.role : "teacher");
    setFormIsActive(String(row.is_active ?? true));
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }

  async function loadTeacherByEmail(targetEmail = lookupEmail) {
    const trimmedEmail = targetEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      setError("Please enter a teacher_email.");
      return;
    }

    setLookupLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await adminGetCorporateTeacherByEmail(trimmedEmail);
      const teacher = getTeacherPayload(response);
      setLookupEmail(trimmedEmail);
      setLookupTeacher(teacher);
      if (teacher?.teacher_email) {
        fillTeacherForm(teacher);
      }
      setSuccess("Teacher loaded.");
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to load teacher.");
      setLookupTeacher(null);
    } finally {
      setLookupLoading(false);
    }
  }

  async function loadTeachersBySchool(targetSchoolId = currentSchoolId || schoolSearchId) {
    const trimmedSchoolId = targetSchoolId.trim();

    if (!trimmedSchoolId) {
      setError("Please enter a school_id.");
      return;
    }

    setListLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await adminListCorporateTeachersBySchool(trimmedSchoolId);
      const rows = getTeachersPayload(response);
      setCurrentSchoolId(trimmedSchoolId);
      setSchoolSearchId(trimmedSchoolId);
      setTeachers(Array.isArray(rows) ? rows : []);
      setSuccess("Teachers loaded.");
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to load teachers.");
      setTeachers([]);
    } finally {
      setListLoading(false);
    }
  }

  async function handleLookupSubmit(event) {
    event.preventDefault();
    await loadTeacherByEmail(lookupEmail);
  }

  async function handleListSubmit(event) {
    event.preventDefault();
    await loadTeachersBySchool(schoolSearchId);
  }

  function validateTeacherForm() {
    const school_id = formSchoolId.trim();
    const teacher_email = formTeacherEmail.trim().toLowerCase();
    const teacher_id = formTeacherId.trim();

    if (!school_id) {
      return { error: "school_id is required." };
    }
    if (!teacher_email) {
      return { error: "teacher_email is required." };
    }
    if (!teacher_id) {
      return { error: "teacher_id is required." };
    }
    if (!ROLE_OPTIONS.includes(formRole)) {
      return { error: "role must be teacher or admin." };
    }

    return {
      payload: {
        school_id,
        teacher_email,
        teacher_id,
        display_name: formDisplayName.trim(),
        role: formRole,
        is_active: formIsActive === "true",
      },
    };
  }

  async function handleSaveTeacher() {
    const validation = validateTeacherForm();
    if (validation.error) {
      setError(validation.error);
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpsertCorporateTeacher(validation.payload);
      setSuccess("Teacher saved.");
      setSelectedTeacher(validation.payload);
      setLookupEmail(validation.payload.teacher_email);
      if (currentSchoolId) {
        await loadTeachersBySchool(currentSchoolId);
      }
      await loadTeacherByEmail(validation.payload.teacher_email);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to save teacher.");
    } finally {
      setActionLoading(false);
    }
  }

  async function updateTeacherStatus(isActive) {
    const teacherEmail = (
      selectedTeacher?.teacher_email ||
      formTeacherEmail ||
      lookupEmail
    ).trim().toLowerCase();

    if (!teacherEmail) {
      setError("Select a teacher or enter a teacher_email first.");
      return;
    }

    if (!isActive) {
      const confirmed = window.confirm(
        `Deactivate ${teacherEmail}? They will no longer be able to log in.`
      );
      if (!confirmed) return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminSetCorporateTeacherStatus({
        school_id: (selectedTeacher?.school_id || formSchoolId || currentSchoolId).trim(),
        teacher_email: teacherEmail,
        teacher_id: selectedTeacher?.teacher_id || formTeacherId.trim(),
        is_active: isActive,
      });
      setSuccess(isActive ? "Teacher activated." : "Teacher deactivated.");
      setFormIsActive(String(isActive));
      if (currentSchoolId) {
        await loadTeachersBySchool(currentSchoolId);
      }
      await loadTeacherByEmail(teacherEmail);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to update teacher status.");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="admin-content">
      <div className="admin-card">
        <h2 className="admin-section-title">Corporate Teachers</h2>
        {error && <div className="admin-error" style={{ marginBottom: 12 }}>{error}</div>}
        {success && <div className="admin-success" style={{ marginBottom: 12 }}>{success}</div>}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Lookup Teacher by Email</h3>
        <form onSubmit={handleLookupSubmit}>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>teacher_email</label>
              <input
                className="admin-input"
                value={lookupEmail}
                onChange={(e) => setLookupEmail(e.target.value)}
                placeholder="teacher@example.com"
              />
            </div>
          </div>
          <div className="admin-button-row">
            <button
              type="submit"
              className="admin-button admin-button-primary"
              disabled={lookupLoading}
            >
              {lookupLoading ? "Loading..." : "Find Teacher"}
            </button>
          </div>
        </form>
        <div style={{ marginTop: 16 }}>
          <TeacherSummary teacher={lookupTeacher} />
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">List Teachers by School</h3>
        <form onSubmit={handleListSubmit}>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>school_id</label>
              <select
                className="admin-input"
                value={schoolSearchId}
                onChange={(e) => setSchoolSearchId(e.target.value)}
                disabled={loadingSchools}
              >
                <option value="">
                  {loadingSchools ? "Loading schools..." : "Select school"}
                </option>
                {schoolOptions.map((school) => {
                  const id = getSchoolId(school);
                  if (!id) return null;
                  return (
                    <option key={id} value={id}>
                      {getSchoolLabel(school)}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="admin-button-row">
            <button
              type="submit"
              className="admin-button admin-button-primary"
              disabled={listLoading}
            >
              {listLoading ? "Loading..." : "Load Teachers"}
            </button>
          </div>
        </form>

        {teachers.length ? (
          <div style={{ overflowX: "auto", marginTop: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  {[
                    "teacher_email",
                    "teacher_id",
                    "display_name",
                    "role",
                    "is_active",
                    "updated_at",
                    "updated_by",
                    "Actions",
                  ].map((heading) => (
                    <th key={heading} style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => (
                  <tr key={`${teacher.teacher_email}-${teacher.teacher_id}`}>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.teacher_email)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.teacher_id)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.display_name)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.role)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.is_active)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.updated_at)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.updated_by)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                      <button
                        type="button"
                        className="admin-button admin-button-secondary"
                        onClick={() => fillTeacherForm(teacher)}
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-meta" style={{ marginTop: 16 }}>
            No teachers loaded.
          </div>
        )}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Add / Update Teacher</h3>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>school_id</label>
            <input
              className="admin-input"
              value={formSchoolId}
              onChange={(e) => setFormSchoolId(e.target.value)}
              placeholder="school_id"
            />
          </div>
          <div className="admin-form-row">
            <label>teacher_email</label>
            <input
              className="admin-input"
              value={formTeacherEmail}
              onChange={(e) => setFormTeacherEmail(e.target.value)}
              placeholder="teacher@example.com"
            />
          </div>
          <div className="admin-form-row">
            <label>teacher_id</label>
            <input
              className="admin-input"
              value={formTeacherId}
              onChange={(e) => setFormTeacherId(e.target.value)}
              placeholder="teacher_id"
            />
          </div>
          <div className="admin-form-row">
            <label>display_name</label>
            <input
              className="admin-input"
              value={formDisplayName}
              onChange={(e) => setFormDisplayName(e.target.value)}
              placeholder="Teacher name"
            />
          </div>
          <div className="admin-form-row">
            <label>role</label>
            <select
              className="admin-input"
              value={formRole}
              onChange={(e) => setFormRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
          <div className="admin-form-row">
            <label>is_active</label>
            <select
              className="admin-input"
              value={formIsActive}
              onChange={(e) => setFormIsActive(e.target.value)}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>
        </div>
        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={handleSaveTeacher}
            disabled={actionLoading}
          >
            Save Teacher
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Activate / Deactivate Selected Teacher</h3>
        <div className="admin-summary-item" style={{ marginBottom: 12 }}>
          <b>teacher_email:</b>{" "}
          {selectedTeacher?.teacher_email || formTeacherEmail || lookupEmail || "-"}
        </div>
        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={() => updateTeacherStatus(true)}
            disabled={actionLoading}
          >
            Activate
          </button>
          <button
            type="button"
            className="admin-button admin-button-danger"
            onClick={() => updateTeacherStatus(false)}
            disabled={actionLoading}
          >
            Deactivate
          </button>
        </div>
      </div>
    </div>
  );
}
